import {
  useCreateTagMutation,
  useDeleteTagMutation,
  useGetTagsQuery,
  useUpdateTagMutation,
  useUpdateWorkspaceTagMutation,
} from '@carboncopyco/data';
import type { Tag, Workspace } from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { useSet } from '@uidotdev/usehooks';
import constate from 'constate';
import { isEqual } from 'lodash-es';
import { useEffect, useState } from 'react';

type WorkspaceManageTagContext = {
  workspace: Workspace;
  organizationId: string;
  isOpen: boolean;
  onClose: (tagsToUpdate?: Tag['id'][]) => void;
};

function useWorkspaceManageTagContext({
  organizationId,
  isOpen,
  onClose,
  workspace,
}: WorkspaceManageTagContext) {
  const selectedTags = useSet<string>();
  const [searchKey, setSearchKey] = useState<string>('');
  const { data: tags = [], isLoading: isLoadingTags } = useGetTagsQuery({
    organizationId,
  });

  const [deleteTag, { isLoading: isLoadingDeleteTag }] = useDeleteTagMutation();
  const [updateTag] = useUpdateTagMutation();
  const [createTag] = useCreateTagMutation();

  const [tagWorkspace, { isLoading: isSaving }] =
    useUpdateWorkspaceTagMutation();

  const resetState = () => {
    setSearchKey('');
    if (workspace?.tags?.length) {
      workspace?.tags?.forEach((tag) => selectedTags?.add(tag));
    }
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchKey('');
      selectedTags.clear();
      return;
    }
    if (workspace?.tags?.length) {
      workspace?.tags?.forEach((tag) => selectedTags?.add(tag));
    }
  }, [isOpen, selectedTags, workspace]);

  const tagsList = searchKey
    ? tags?.filter(
        (tag) => tag.name.toLowerCase().indexOf(searchKey.toLowerCase()) !== -1
      )
    : tags;

  const list = [...tagsList];

  const submit = () => {
    selectedTags.delete('');
    const tagsToUpdate = [...selectedTags];
    tagWorkspace({
      organizationId,
      id: workspace.id,
      tags: tagsToUpdate,
    })
      .unwrap()
      .then(() => {
        onClose(tagsToUpdate);
        notify.success('Workspace settings updated successfully');
      })
      .catch(() => {
        notify.success('Failed to update workspace settings');
      })
      .finally(resetState);
  };

  const clear = () => {
    tagsList?.forEach((tag) => selectedTags.delete(tag.id));
  };

  const selectAll = () => {
    tagsList?.forEach((tag) => selectedTags.add(tag.id));
  };

  const isDirty = !isEqual(workspace?.tags ?? [], [...selectedTags]);

  const isLoading = isLoadingTags;
  const searchKeyExists = list.find(
    (tag) => tag.name.toLowerCase() === searchKey.toLowerCase()
  );

  return {
    isLoading,
    isLoadingDeleteTag,
    isSaving,
    isDirty,
    list,
    selectedTags,
    searchKey,
    searchKeyExists,
    setSearchKey,
    tags,
    workspace,
    organizationId,
    isOpen,
    onClose,
    submit,
    clear,
    selectAll,
    workspaceName: workspace?.workspaceName,
    deleteTag: (tagId: string) => {
      deleteTag({ id: tagId, organizationId })
        .unwrap()
        .then(() => {
          notify.success('Tag deleted successfully');
        })
        .catch(() => notify.error('Failed to delete tag'));
    },
    updateTag: (tagId: string, name: string) => {
      updateTag({ id: tagId, organizationId, name })
        .unwrap()
        .then(() => notify.success('Tag updated successfully'))
        .catch(() => notify.error('Failed to update tag'));
    },
    createTag: () => {
      createTag({ name: searchKey, organizationId })
        .unwrap()
        .then(() => {
          setSearchKey('');
          notify.success('Tag created successfully');
        })
        .catch(() => {
          notify.error('Failed to create tag');
        });
    },
  };
}

export const [WorkspaceManageTagProvider, useWorkspaceManageTag] = constate(
  useWorkspaceManageTagContext
);
